import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Btn, Field, inputCls, Card, Modal } from "@/components/cc";
import StatusBadge from "@/components/StatusBadge";
import { CheckCircle2, XCircle, RefreshCw } from "lucide-react";

export default function Editorial() {
  const [items, setItems] = useState([]);
  const [reviewFor, setReviewFor] = useState(null);
  const [form, setForm] = useState({ decision: "approve", comments: "" });

  const load = () => api.get("/content", { params: { status: "editorial_review" } }).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/content/${reviewFor.id}/review`, form);
      toast.success(`Review submitted: ${form.decision}`);
      setReviewFor(null);
      setForm({ decision: "approve", comments: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="p-6">
      <PageHeader title="Editorial Review" subtitle="Content awaiting review before publication." />
      <div className="space-y-2" data-testid="editorial-queue">
        {items.length === 0 && <p className="text-sm text-zinc-600">Nothing awaiting review.</p>}
        {items.map((c) => (
          <Card key={c.id} className="p-4 flex items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="capitalize text-xs text-zinc-500">{c.type.replace("_", "-")}</span>
                <StatusBadge status={c.status} />
              </div>
              <h3 className="mt-1 font-medium text-zinc-100">{c.title}</h3>
              <p className="text-sm text-zinc-500 line-clamp-1">{c.summary}</p>
            </div>
            <Link to={`/command-center/studio/${c.id}`} className="text-sm text-zinc-400 hover:text-white">Open</Link>
            <Btn data-testid={`review-btn-${c.id}`} onClick={() => setReviewFor(c)}>Review</Btn>
          </Card>
        ))}
      </div>

      <Modal open={!!reviewFor} onClose={() => setReviewFor(null)} title={`Review: ${reviewFor?.title || ""}`}>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Decision">
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "approve", l: "Approve", icon: CheckCircle2, cls: "text-emerald-400 border-emerald-600" },
                { v: "request_changes", l: "Changes", icon: RefreshCw, cls: "text-amber-400 border-amber-600" },
                { v: "reject", l: "Reject", icon: XCircle, cls: "text-red-400 border-red-600" },
              ].map((d) => (
                <button key={d.v} type="button" onClick={() => setForm({ ...form, decision: d.v })}
                  data-testid={`decision-${d.v}`}
                  className={`flex flex-col items-center gap-1 py-3 rounded-sm border text-xs ${form.decision === d.v ? `bg-zinc-800 ${d.cls}` : "border-zinc-700 text-zinc-500"}`}>
                  <d.icon className="h-4 w-4" /> {d.l}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Comments"><textarea data-testid="review-comments-input" className={inputCls} rows={3} value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} /></Field>
          <Btn data-testid="review-submit-btn" type="submit" className="w-full justify-center">Submit Review</Btn>
        </form>
      </Modal>
    </div>
  );
}
